/**
 * CardCountdown - Décompte animé avant une nouvelle donne
 *
 * Affiche les chiffres 5→1 au centre du tapis, chaque chiffre étant
 * "dessiné" par des cartes de tarot posées progressivement le long
 * d'un tracé SVG. À chaque transition, les cartes "explosent" dans
 * toutes les directions (CSS transitions) avec une intensité croissante.
 */

import { useState, useEffect, useRef } from 'react';
import { getCardImagePath } from '../utils/cards';
import '../styles/card-countdown.css';

// ============================================
// Types
// ============================================

interface CardCountdownProps {
  startFrom?: number;
  /** Horodatage serveur (Date.now()) visé — le décompte s'y cale avec une marge */
  deadlineAt?: number;
  onComplete?: () => void;
}

interface DigitTiming {
  drawMs: number;
  pauseMs: number;
  lastPauseMs: number;
  explodeMs: number;
}

/** Caler le décompte sur le timer annoncé par le serveur Java (souvent un peu plus court). */
function computeDigitTiming(startFrom: number, deadlineAt?: number): DigitTiming {
  const MARGIN_MS = 200;
  const totalMs = deadlineAt
    ? Math.max(startFrom * 400, deadlineAt - Date.now() - MARGIN_MS)
    : startFrom * 640 + 200;
  const perDigit = totalMs / startFrom;
  return {
    drawMs: Math.max(280, Math.round(perDigit * 0.7)),
    pauseMs: Math.max(60, Math.round(perDigit * 0.15)),
    lastPauseMs: Math.max(120, Math.round(perDigit * 0.28)),
    explodeMs: Math.min(380, Math.round(perDigit * 0.22)),
  };
}

interface SampledPoint {
  x: number;
  y: number;
  angle: number;
}

interface ExplodingState {
  points: SampledPoint[];
  cardOffset: number;
  seeds: { ex: number; ey: number; er: number }[];
  duration: number;
}

// ============================================
// Constantes
// ============================================

const CARDS_PER_DIGIT = 24;

const CARD_W = 28;
const CARD_H = 51;

/** Zone de rendu du chiffre (SVG viewBox 0 0 100 150 → pixels) */
const DISPLAY_W = 200;
const DISPLAY_H = 300;

// ============================================
// Tracés SVG des chiffres (viewBox 0 0 100 150)
// ============================================

const DIGIT_PATHS: Record<number, string[]> = {
  0: [
    'M 50,10 C 82,10 92,45 92,75 C 92,105 82,145 50,145 C 18,145 8,105 8,75 C 8,45 18,10 50,10',
  ],
  1: [
    'M 30,30 L 55,8 L 55,142',
  ],
  2: [
    'M 15,42 C 15,12 88,8 88,42 C 88,72 20,100 12,135 L 90,135',
  ],
  3: [
    'M 18,20 C 48,4 90,10 85,45 C 82,62 60,70 50,72',
    'M 50,72 C 65,74 92,85 90,115 C 88,142 52,152 18,132',
  ],
  4: [
    'M 22,8 L 8,98 L 88,98',
    'M 68,22 L 68,142',
  ],
  5: [
    'M 82,12 L 22,12 L 18,72 C 38,55 82,52 90,85 C 98,118 65,150 28,140',
  ],
};

// ============================================
// Échantillonnage des tracés SVG
// ============================================

function sampleDigitPaths(pathStrings: string[], numPoints: number): SampledPoint[] {
  const svgNS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNS, 'svg');
  svg.style.position = 'absolute';
  svg.style.width = '0';
  svg.style.height = '0';
  svg.style.overflow = 'hidden';
  document.body.appendChild(svg);

  const pathEls: SVGPathElement[] = [];
  let totalLen = 0;

  for (const d of pathStrings) {
    const p = document.createElementNS(svgNS, 'path');
    p.setAttribute('d', d);
    svg.appendChild(p);
    pathEls.push(p);
    totalLen += p.getTotalLength();
  }

  const points: SampledPoint[] = [];

  for (const p of pathEls) {
    const len = p.getTotalLength();
    const count = Math.max(2, Math.round((len / totalLen) * numPoints));

    for (let i = 0; i < count; i++) {
      const t = (i / (count - 1)) * len;
      const pt = p.getPointAtLength(t);
      const pt2 = p.getPointAtLength(Math.min(t + 0.5, len));
      const angle = Math.atan2(pt2.y - pt.y, pt2.x - pt.x) * (180 / Math.PI);
      points.push({ x: pt.x, y: pt.y, angle });
    }
  }

  document.body.removeChild(svg);
  return points;
}

// ============================================
// Helpers
// ============================================

function cardJitter(index: number, cardNum: number): number {
  return ((index * 7 + cardNum) % 11) - 5;
}

// ============================================
// Composant
// ============================================

export default function CardCountdown({ startFrom = 5, deadlineAt, onComplete }: CardCountdownProps) {
  const [currentDigit, setCurrentDigit] = useState(startFrom);
  const [visibleCount, setVisibleCount] = useState(0);
  const [phase, setPhase] = useState<'draw' | 'pause' | 'done'>('draw');
  const [exploding, setExploding] = useState<ExplodingState | null>(null);
  const [explodeActive, setExplodeActive] = useState(false);

  const allPointsRef = useRef<Map<number, SampledPoint[]>>(new Map());
  const cardsRef = useRef<number[]>([]);
  const cardIndexRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const timingRef = useRef<DigitTiming>(computeDigitTiming(startFrom, deadlineAt));

  // ---- Pré-calcul au montage ----
  useEffect(() => {
    const map = new Map<number, SampledPoint[]>();
    for (let d = 0; d <= startFrom; d++) {
      const paths = DIGIT_PATHS[d];
      if (paths) {
        map.set(d, sampleDigitPaths(paths, CARDS_PER_DIGIT));
      }
    }
    allPointsRef.current = map;

    const totalCards = CARDS_PER_DIGIT * (startFrom + 1);
    const cards: number[] = [];
    for (let i = 0; i < totalCards; i++) {
      cards.push(Math.floor(Math.random() * 78) + 1);
    }
    cardsRef.current = cards;
  }, [startFrom]);

  // ---- Phase "draw" : pose progressive des cartes ----
  useEffect(() => {
    if (phase !== 'draw') return;

    const points = allPointsRef.current.get(currentDigit);
    if (!points || points.length === 0) {
      setPhase('pause');
      return;
    }

    const { drawMs, explodeMs } = timingRef.current;
    const interval = drawMs / points.length;
    let count = 0;

    const timer = setInterval(() => {
      count++;
      setVisibleCount(count);
      if (count >= points.length) {
        clearInterval(timer);

        // Construire l'explosion et basculer en pause dans le même batch React
        // pour éviter un flash (frame sans cartes).
        const duration = explodeMs;

        const seeds = points.map(() => {
          const angle = Math.random() * Math.PI * 2;
          const dist = 200 + Math.random() * 350;
          return {
            ex: Math.cos(angle) * dist,
            ey: Math.sin(angle) * dist,
            er: (Math.random() - 0.5) * 720,
          };
        });

        setExploding({ points, cardOffset: cardIndexRef.current, seeds, duration });
        setPhase('pause');
      }
    }, interval);

    return () => clearInterval(timer);
  }, [phase, currentDigit, startFrom]);

  // ---- Phase "pause" : attente puis transition vers le chiffre suivant ----
  useEffect(() => {
    if (phase !== 'pause') return;

    const isLast = currentDigit <= 1;
    const { pauseMs, lastPauseMs } = timingRef.current;
    const pauseTime = isLast ? lastPauseMs : pauseMs;

    const timer = setTimeout(() => {
      if (!isLast) {
        const points = allPointsRef.current.get(currentDigit);
        cardIndexRef.current += points?.length ?? 0;
        setCurrentDigit(prev => prev - 1);
        setVisibleCount(0);
        setPhase('draw');
      } else {
        setPhase('done');
        onCompleteRef.current?.();
      }
    }, pauseTime);

    return () => clearTimeout(timer);
  }, [phase, currentDigit, startFrom]);

  // ---- Déclenchement de la transition d'explosion après le premier paint ----
  useEffect(() => {
    if (!exploding) {
      setExplodeActive(false);
      return;
    }
    // Double rAF : le premier frame rend les cartes à leur position initiale,
    // le second déclenche la transition CSS vers la position d'explosion.
    let cancelled = false;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) setExplodeActive(true);
      });
    });
    return () => { cancelled = true; };
  }, [exploding]);

  // ---- Nettoyage des cartes d'explosion après fin de l'animation ----
  useEffect(() => {
    if (!exploding) return;
    const timer = setTimeout(() => setExploding(null), exploding.duration + 150);
    return () => clearTimeout(timer);
  }, [exploding]);

  // ---- Rendu ----

  if (phase === 'done') return null;

  const scaleX = DISPLAY_W / 100;
  const scaleY = DISPLAY_H / 150;
  const points = allPointsRef.current.get(currentDigit);

  return (
    <div className="card-countdown-overlay">
      <div
        className="card-countdown-digit"
        style={{ width: DISPLAY_W, height: DISPLAY_H }}
      >
        {/* Cartes du chiffre en cours de dessin */}
        {phase === 'draw' && points && points.slice(0, visibleCount).map((pt, i) => {
          const cardNum = cardsRef.current[(cardIndexRef.current + i) % cardsRef.current.length];
          const left = pt.x * scaleX - CARD_W / 2;
          const top = pt.y * scaleY - CARD_H / 2;
          const jitter = cardJitter(i, cardNum);

          return (
            <img
              key={`${currentDigit}-${i}`}
              src={getCardImagePath(cardNum as any)}
              alt=""
              className="countdown-card"
              draggable={false}
              style={{
                width: CARD_W,
                height: CARD_H,
                left,
                top,
                zIndex: i + 1,
                transform: `rotate(${pt.angle + jitter}deg)`,
              }}
            />
          );
        })}

        {/* Cartes en explosion (CSS transition) */}
        {exploding && exploding.points.map((pt, i) => {
          const cardNum = cardsRef.current[(exploding.cardOffset + i) % cardsRef.current.length];
          const left = pt.x * scaleX - CARD_W / 2;
          const top = pt.y * scaleY - CARD_H / 2;
          const jitter = cardJitter(i, cardNum);
          const { ex, ey, er } = exploding.seeds[i];

          return (
            <img
              key={`explode-${i}`}
              src={getCardImagePath(cardNum as any)}
              alt=""
              className="countdown-card-explosion"
              draggable={false}
              style={{
                width: CARD_W,
                height: CARD_H,
                left,
                top,
                zIndex: 100 + i,
                transitionDuration: `${exploding.duration}ms`,
                transitionDelay: `${i * 4}ms`,
                transform: explodeActive
                  ? `translate(${ex}px, ${ey}px) rotate(${er}deg) scale(0.1)`
                  : `rotate(${pt.angle + jitter}deg)`,
                opacity: explodeActive ? 0 : 1,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
